import { cn } from "@/lib/utils";
import { CircleX } from "lucide-react";

import "../../../styles/file-record-card-unavailable.css";

export interface FileRecordCardUnavailableProps {
  width: number;
  height?: number;
  /** The node's own label — the reference row still knows its name. */
  label?: string;
  className?: string;
}

/**
 * A file-record node whose file can no longer be read: the target was
 * archived, or the viewer has no access to it. Both render this, deliberately
 * — telling them apart would report whether a file you cannot see exists.
 *
 * The state belongs to the object, not only to a badge: a card still drawing
 * its old artwork under a small chip would be lying about what it is. The
 * body carries it alone — at 22px the glyph reads unaided, so the treatment
 * stays neutral and the board never lights up. The footer keeps the name and
 * nothing else; repeating the mark at 13px under a card that is already
 * hatched and marked reads as two indicators for one state.
 *
 * Deviates from canvas-object-labels §11, which specs both marks with the
 * tone split by size, by operator decision.
 *
 * Render-time only: nothing about this is written back to the canvas
 * document, so restoring the file heals every node pointing at it.
 */
export function FileRecordCardUnavailable({
  width,
  height,
  label,
  className,
}: FileRecordCardUnavailableProps) {
  return (
    <div
      role="status"
      aria-label={label ? `${label} — unavailable` : "Unavailable"}
      className={cn(
        "relative flex flex-col overflow-hidden rounded-xl bg-card",
        className,
      )}
      style={{ width, height }}
    >
      <div className="file-record-unavailable__body relative flex-1 min-h-0 flex flex-col items-center justify-center gap-2 px-3 text-muted-foreground">
        <CircleX className="h-[22px] w-[22px] shrink-0" aria-hidden />
        <span className="text-[11px] leading-[1.4] text-center">
          No longer available
        </span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 px-2.5 py-2 min-w-0">
        {label && (
          <p className="min-w-0 truncate text-[12px] font-semibold leading-tight text-muted-foreground">
            {label}
          </p>
        )}
      </div>
    </div>
  );
}

export default FileRecordCardUnavailable;
